/**
 * useAnalyticsBeacon — fires a page_view event on every route change.
 *
 * Mount once at the App level. Listens to react-router's location and emits
 * a beacon when the pathname changes (debounced 200ms to avoid double-fires
 * on transient redirects).
 *
 * Skips:
 *   - /config/* (admin routes — not interesting for business analytics)
 *   - /__preview/* (internal preview routes)
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../lib/analyticsBeacon";

const IGNORED_PREFIXES = ["/config", "/__preview", "/api"];

export default function useAnalyticsBeacon() {
    const { pathname } = useLocation();
    const lastPath = useRef(null);
    const timer = useRef(null);

    useEffect(() => {
        if (!pathname) return;
        if (IGNORED_PREFIXES.some(p => pathname.startsWith(p))) return;
        if (lastPath.current === pathname) return;

        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            lastPath.current = pathname;
            trackPageView(pathname);
        }, 200);

        return () => { if (timer.current) clearTimeout(timer.current); };
    }, [pathname]);
}
